/**
 * @fileoverview Password validation utilities (framework-agnostic).
 * @summary Shared password strength validation for both backend and frontend.
 *
 * @package @karaonow/shared
 * @module validation/password
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
/**
 * @summary Password validation error codes.
 */
export var PasswordErrorCode;
(function (PasswordErrorCode) {
    PasswordErrorCode["MIN_LENGTH"] = "PASSWORD_MIN_LENGTH";
    PasswordErrorCode["NO_UPPERCASE"] = "PASSWORD_NO_UPPERCASE";
    PasswordErrorCode["NO_LOWERCASE"] = "PASSWORD_NO_LOWERCASE";
    PasswordErrorCode["NO_DIGIT"] = "PASSWORD_NO_DIGIT";
    PasswordErrorCode["NO_SPECIAL_CHAR"] = "PASSWORD_NO_SPECIAL_CHAR";
})(PasswordErrorCode || (PasswordErrorCode = {}));
/**
 * @summary Validate password strength using simple character class rules.
 * @description
 * Requirements:
 * - Minimum length: 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one special character (non alphanumeric)
 *
 * This function is intentionally framework-agnostic so it can be reused
 * in both frontend and backend with the same validation logic.
 * Error messages are not included - use error codes with i18n translation.
 *
 * @param {string} password - Plain text password to validate.
 * @returns {PasswordValidationResult} Validation result with error codes (not messages).
 */
export function validatePasswordStrength(password) {
    const errors = [];
    if (!password || password.length < 12) {
        errors.push(PasswordErrorCode.MIN_LENGTH);
    }
    if (!/[A-Z]/.test(password)) {
        errors.push(PasswordErrorCode.NO_UPPERCASE);
    }
    if (!/[a-z]/.test(password)) {
        errors.push(PasswordErrorCode.NO_LOWERCASE);
    }
    if (!/[0-9]/.test(password)) {
        errors.push(PasswordErrorCode.NO_DIGIT);
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
        errors.push(PasswordErrorCode.NO_SPECIAL_CHAR);
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * @summary Check if password is valid.
 * @param {string} password - Password to check.
 * @returns {boolean} True if password is valid.
 */
export function isPasswordValid(password) {
    return validatePasswordStrength(password).valid;
}
/**
 * @summary Get password strength percentage.
 * @param {string} password - Password to check.
 * @returns {number} Percentage from 0 to 100.
 */
export function getPasswordStrength(password) {
    const result = validatePasswordStrength(password);
    if (result.valid)
        return 100;
    // Count how many requirements are met
    let score = 0;
    if (password && password.length >= 12)
        score += 20;
    if (/[A-Z]/.test(password))
        score += 20;
    if (/[a-z]/.test(password))
        score += 20;
    if (/[0-9]/.test(password))
        score += 20;
    if (/[^A-Za-z0-9]/.test(password))
        score += 20;
    return score;
}
//# sourceMappingURL=password.js.map